/* global BigInt */
import { Web3 } from "web3";
import { ReadProvider } from "./readProvider";
import saleABI from "./abi/ADBYTESPublicSale.json";
import receiptABI from "./abi/ADBYTESVestingNFT.json";
import vaultABI from "./abi/ADBYTESLiquidityVault.json";
import nativeABI from "./abi/ADBYTESNativeBuyer.json";
import tokenABI from "../abi/ADBYTES.json";
import { getInjectedProvider } from "../utils/walletProvider";
import { scanOwnedEvents } from "./model";
import { getGasFees } from "../utils/gasFees";
export class BondService {
  constructor(config) {
    if (!Number.isInteger(config.deploymentBlock) || config.deploymentBlock < 0)
      throw new Error("Set the sale deployment block in bond configuration.");
    if (
      config.logRange !== undefined &&
      (!Number.isInteger(config.logRange) ||
        config.logRange < 1 ||
        config.logRange > 10000)
    )
      throw new Error("Invalid event log range.");
    this.config = config;
    this.web3 = new Web3(new ReadProvider(config));
    this.sale = new this.web3.eth.Contract(saleABI, config.sale);
    this.receipt = new this.web3.eth.Contract(receiptABI, config.receipt);
    this.vault = new this.web3.eth.Contract(vaultABI, config.vault);
    this.usdc = new this.web3.eth.Contract(tokenABI, config.usdc);
    this.native = config.nativeBuyer
      ? new this.web3.eth.Contract(nativeABI, config.nativeBuyer)
      : null;
  }
  async state() {
    const block = await this.web3.eth.getBlock("latest");
    const at = block.number;
    if (
      Number(await this.web3.eth.getChainId()) !== Number(this.config.chainId)
    )
      throw new Error("RPC network does not match the sale.");
    const values = await Promise.all(
      [
        "soldTokens",
        "inventory",
        "grossReceipts",
        "paused",
        "closed",
        "treasury",
      ].map((key) => this.sale.methods[key]().call({}, at)),
    );
    const [sold, inventory, gross, paused, closed, treasury] = values;
    // These dependencies are immutable in this non-upgradeable deployment.
    // Validate once per service instance; continue polling all live sale state.
    if (!this.dependenciesVerified) {
      const [token, receipt, usdc, vault] =
        await Promise.all([
          this.sale.methods.asset().call({}, at),
          this.sale.methods.receipt().call({}, at),
          this.sale.methods.usdc().call({}, at),
          this.sale.methods.liquidityVault().call({}, at),
        ]);
      for (const [actual, expected] of [
        [token, this.config.token],
        [receipt, this.config.receipt],
        [usdc, this.config.usdc],
        [vault, this.config.vault],
      ])
        if (actual.toLowerCase() !== expected.toLowerCase())
          throw new Error(
            "Sale addresses do not match the configured deployment.",
          );
      if (
        this.native &&
        (await this.native.methods.sale().call({}, at)).toLowerCase() !==
          this.config.sale.toLowerCase()
      )
        throw new Error("ETH adapter does not match this sale.");
      const identities = [
        [this.vault, "pool", this.config.pool],
        [this.vault, "quoter", this.config.quoter],
        [this.vault, "router", this.config.router],
        [this.vault, "positionManager", this.config.positionManager],
      ];
      if (this.native)
        identities.push(
          [this.native, "router", this.config.router],
          [this.native, "quoter", this.config.quoter],
        );
      await Promise.all(
        identities.map(async ([contract, method, expected]) => {
          if (
            !expected ||
            (await contract.methods[method]().call({}, at)).toLowerCase() !==
              expected.toLowerCase()
          )
            throw new Error(
              "V3 deployment dependencies do not match configuration.",
            );
        }),
      );
      if (
        this.native &&
        Number(await this.native.methods.poolFee().call({}, at)) !==
          this.config.nativePoolFee
      )
        throw new Error("ETH pool fee does not match configuration.");
      this.dependenciesVerified = true;
    }
    const liquidityPaused = await this.vault.methods.paused().call({}, at);
    return {
      sold,
      inventory,
      gross,
      paused: paused || liquidityPaused,
      closed,
      treasury,
      timestamp: block.timestamp,
      block: at,
    };
  }
  async quote(amount, tier) {
    const block = await this.web3.eth.getBlockNumber();
    const [cost, sold] = await Promise.all([
      this.sale.methods.quoteExactTokens(amount, tier).call({}, block),
      this.sale.methods.soldTokens().call({}, block),
    ]);
    const q = await this.vault.methods.quoteLiquidity((cost * 3n) / 10n).call({gas:25000000}, block);
    const liquidityPlan = [q.swapUSDC, q.tokensOut * 99n / 100n, q.liquidity * 99n / 100n];
    return { amount: BigInt(amount), tier, cost, liquidityPlan, block, sold };
  }
  async nativeQuote(amount, tier, block = "latest") {
    if (!this.native)
      throw new Error("ETH payments are not configured for this deployment.");
    return this.native.methods.quoteETH(amount, tier).call({}, block);
  }
  async bonds(account, block) {
    this.receiptReads ||= new Map();
    const key = account.toLowerCase();
    // A slow initial scan must finish rather than restart on every UI poll.
    if (this.receiptReads.has(key)) {
      const pending = this.receiptReads.get(key);
      const rows = await pending.promise;
      return Number(block) > Number(pending.block) ? this.bonds(account, block) : rows;
    }
    const read = this.readBonds(account, block);
    const pending = { block, promise: read };
    this.receiptReads.set(key, pending);
    try {
      return await read;
    } finally {
      if (this.receiptReads.get(key) === pending) this.receiptReads.delete(key);
    }
  }
  async readBonds(account, block) {
    this.receiptCache ||= new Map();
    const key = account.toLowerCase(),
      at = Number(block);
    if (at < this.config.deploymentBlock)
      throw new Error("Sale deployment block is ahead of this RPC.");
    const target = await this.web3.eth.getBlock(block);
    if (!target?.hash)
      throw new Error("Could not confirm the receipt snapshot block.");
    let cached = this.receiptCache.get(key);
    if (
      cached &&
      (cached.block > at ||
        (await this.web3.eth.getBlock(cached.block))?.hash !== cached.hash)
    ) {
      this.receiptCache.delete(key);
      cached = null;
    }
    // Small deployments need only a few state reads, not days of Transfer logs.
    // Burned IDs are excluded only on an explicit ERC721 invalid-ID revert.
    {
      const next = BigInt(await this.receipt.methods.nextId().call({}, block));
      if (next <= 1001n) {
        const readBatch = async (ids) => {
          if (!ids.length) return [];
          try {
            const infos = await this.receipt.methods.positionsInfo(ids).call({}, block);
            return ids.flatMap((id, i) => infos[i].owner.toLowerCase() === key
              ? [{ id, ...infos[i], snapshotBlock: block }] : []);
          } catch (error) {
            const messages = [];
            for (let e = error, depth = 0; e && depth < 8; e = e.cause || e.innerError, depth++) messages.push(e.message);
            if (!/invalid token ID|Unknown receipt/i.test(messages.join(" "))) throw error;
            if (ids.length === 1) return [];
            const middle = Math.floor(ids.length / 2);
            return [...await readBatch(ids.slice(0, middle)), ...await readBatch(ids.slice(middle))];
          }
        };
        const rows = [];
        for (let start = 1; start < Number(next); start += 100) {
          const ids = Array.from({ length: Math.min(100, Number(next) - start) }, (_, i) => String(start + i));
          rows.push(...await readBatch(ids));
        }
        if ((await this.web3.eth.getBlock(block))?.hash !== target.hash)
          throw new Error("Chain changed while reading receipts. Retrying on the next refresh.");
        this.receiptCache.set(key, { block: at, hash: target.hash, ids: rows.map(row => row.id) });
        return rows.sort((a, b) => Number(BigInt(b.id) - BigInt(a.id)));
      }
    }
    // Chunk logs for RPC limits. Rebuild ownership so outgoing transfers and burns disappear.
    let ids = cached?.ids || [];
    const span = Number(this.config.logRange || 2000);
    for (
      let start = cached
        ? cached.block + 1
        : Number(this.config.deploymentBlock);
      start <= Number(block);
      start += span
    ) {
      const range = {
        fromBlock: start,
        toBlock: Math.min(start + span - 1, Number(block)),
      };
      const [incoming, outgoing] = await Promise.all([
        this.receipt.getPastEvents("Transfer", {
          ...range,
          filter: { to: account },
        }),
        this.receipt.getPastEvents("Transfer", {
          ...range,
          filter: { from: account },
        }),
      ]);
      ids = scanOwnedEvents([...incoming, ...outgoing], account, ids);
      // Checkpoint each complete chunk: an RPC limit must not restart the scan.
      const checkpoint = await this.web3.eth.getBlock(range.toBlock);
      if (!checkpoint?.hash) throw new Error("Could not confirm receipt scan progress.");
      this.receiptCache.set(key, { block: range.toBlock, hash: checkpoint.hash, ids });
      if (range.toBlock < at) await new Promise(resolve => setTimeout(resolve, 250));
    }

    const rows = [];
    for (let i = 0; i < ids.length; i += 100) {
      const batch = ids.slice(i, i + 100),
        infos = await this.receipt.methods.positionsInfo(batch).call({}, block);
      batch.forEach((id, j) => {
        if (infos[j].owner.toLowerCase() === account.toLowerCase())
          rows.push({ id, ...infos[j], snapshotBlock: block });
      });
    }
    if ((await this.web3.eth.getBlock(block))?.hash !== target.hash)
      throw new Error(
        "Chain changed while reading receipts. Retrying on the next refresh.",
      );
    this.receiptCache.set(key, { block: at, hash: target.hash, ids });
    if (this.receiptCache.size > 8)
      this.receiptCache.delete(this.receiptCache.keys().next().value);
    return rows.sort((a, b) => Number(BigInt(b.id) - BigInt(a.id)));
  }
  async wallet(expected) {
    const provider = getInjectedProvider();
    if (!provider) throw new Error("Connect a browser wallet first.");
    const accounts = await provider.request({ method: "eth_accounts" });
    if (!accounts[0] || accounts[0].toLowerCase() !== expected.toLowerCase())
      throw new Error("Wallet account changed. Refresh your quote.");
    if (
      Number(await provider.request({ method: "eth_chainId" })) !==
      Number(this.config.chainId)
    )
      throw new Error(`Switch your wallet to ${this.config.chainName}.`);
    return new Web3(provider);
  }
  async send(abi, address, method, args, account, onStatus, value = 0n) {
    const web3 = await this.wallet(account);
    const fn = new web3.eth.Contract(abi, address).methods[method](...args);
    const gas = await fn.estimateGas({ from: account, value });
    const fees = await getGasFees(web3);
    await this.wallet(account); // Re-check after estimation, before requesting a signature.
    if (getInjectedProvider() !== web3.currentProvider)
      throw new Error("Selected wallet changed. Please retry.");
    onStatus("Confirm in your wallet…");
    let submittedHash;
    try {
      const tx = await fn.send({
        from: account, value, chainId: this.config.chainId,
        gas: (gas * 125n) / 100n + 10000n, ...fees,
      }).on("transactionHash", (hash) => {
        submittedHash = hash;
        onStatus("Transaction submitted. Waiting for confirmation…", hash);
      });
      if (!tx.status) {
        const error = new Error("Transaction reverted.");
        error.receipt = tx;
        throw error;
      }
      return tx;
    } catch (cause) {
      const hash = submittedHash || cause.receipt?.transactionHash;
      let receipt = cause.receipt;
      if (hash && !receipt && this.web3?.eth?.getTransactionReceipt) {
        try { receipt = await this.web3.eth.getTransactionReceipt(hash); }
        catch { /* Preserve the hash and report uncertainty if the read also fails. */ }
      }
      if (receipt && BigInt(receipt.status) === 1n) return receipt;
      const error = new Error(receipt ? "Transaction reverted." : cause.message || "Transaction confirmation failed.");
      error.cause = receipt ? undefined : cause;
      error.transactionHash = hash;
      // A transport failure after broadcast is not evidence of a reverted purchase.
      error.transactionPending = Boolean(error.transactionHash && !receipt);
      throw error;
    }
  }
  async purchase(quote, payment, account, onStatus, ethMax = 0n) {
    await this.wallet(account);
    const state = await this.state();
    if (state.paused || state.closed)
      throw new Error("Purchases are currently paused or closed.");
    if (quote.amount > state.inventory)
      throw new Error("This allocation is no longer available.");
    const current = await this.quote(quote.amount, quote.tier);
    if (current.cost !== quote.cost)
      throw new Error(
        "The curve has advanced. Review the new quote before purchasing.",
      );
    let deadline = state.timestamp + 600n;
    if (payment === "ETH" && (await this.web3.eth.getBalance(account)) < ethMax)
      throw new Error(
        "Your wallet needs more ETH for this payment, plus network gas.",
      );
    if (
      payment === "USDC" &&
      (await this.usdc.methods.balanceOf(account).call()) < quote.cost
    )
      throw new Error(
        "Your wallet does not have enough USDC for this allocation.",
      );
    if (payment !== "ETH" && payment !== "USDC")
      throw new Error("Unsupported payment currency.");
    if (payment === "ETH")
      return this.send(
        nativeABI,
        this.config.nativeBuyer,
        "buyWithETH",
        [quote.amount, quote.tier, quote.cost, deadline, current.liquidityPlan],
        account,
        (message, hash) => onStatus(message, hash, "purchase"),
        ethMax,
      );
    const allowance = await this.usdc.methods
      .allowance(account, this.config.sale)
      .call();
    if (allowance < quote.cost) {
      onStatus(
        "Step 1 of 2: approve USDC for this purchase.",
        null,
        "approval",
      );
      await this.send(
        tokenABI,
        this.config.usdc,
        "approve",
        [this.config.sale, quote.cost],
        account,
        (message, hash) =>
          onStatus(`USDC approval: ${message}`, hash, "approval"),
      );
      const confirmed = await this.usdc.methods
        .allowance(account, this.config.sale)
        .call();
      if (confirmed < quote.cost)
        throw new Error(
          "USDC approval did not cover the purchase. Please retry.",
        );
    }
    // Approval may take minutes. Revalidate the sale before requesting payment.
    await this.wallet(account);
    const latest = await this.state();
    if (latest.paused || latest.closed)
      throw new Error(
        "The sale is paused or closed. Your USDC approval remains available.",
      );
    if (quote.amount > latest.inventory)
      throw new Error(
        "This allocation is no longer available. Choose a smaller amount.",
      );
    const refreshed = await this.quote(quote.amount, quote.tier);
    if (refreshed.cost !== quote.cost)
      throw new Error(
        "The price changed during approval. Review a fresh quote; no purchase was made.",
      );
    deadline = latest.timestamp + 600n;
    onStatus("Confirm your purchase in the wallet.", null, "purchase");
    return this.send(
      saleABI,
      this.config.sale,
      "buyExactTokens",
      [quote.amount, quote.tier, quote.cost, deadline, account, refreshed.liquidityPlan],
      account,
      (message, hash) => onStatus(message, hash, "purchase"),
    );
  }
  async claim(id, account, onStatus) {
    return this.send(
      receiptABI,
      this.config.receipt,
      "claim",
      [id],
      account,
      onStatus,
    );
  }
  async transfer(id, to, minimum, account, onStatus) {
    if (!Web3.utils.isAddress(to))
      throw new Error("Enter a valid recipient address.");
    return this.send(
      receiptABI,
      this.config.receipt,
      "safeTransferWithMinimumRemaining",
      [account, to, id, minimum],
      account,
      onStatus,
    );
  }
}
